/**
 * Client networking: connect, handshake, send input/ping, receive snapshots.
 * Owns the snapshot ring buffer + a smoothed server-time estimate, and exposes
 * interpolated remote-entity state (INTERP_DELAY_MS in the past) for rendering.
 * The LOCAL ship is NOT interpolated here — it is predicted (see prediction.ts).
 */
import {
  INTERP_DELAY_MS, MsgType,
  BinaryReader, decodeBases, decodeClan, decodeMarks, decodeLeaderboard, decodeProfile, decodePong, decodeSnapshot, decodeWelcome, decodeWorld, decodeReject, decodeRaidResult, decodeMarketState, decodeTerritory, decodeWallet, decodeContracts, decodePremium, encodePremiumReq, decodeSor, encodeSorReq, decodeGoods, decodeProfessions, decodeJournal, decodeFreight, decodeAuction, decodeWarJournal, encodeRevengeReq, RevengeOp, decodeSeason, encodeSeasonReq, decodeCodex, encodeCodexReq, decodeDawn, decodeCalendar, decodeProfLeader, PROFESSION_KEYS,
  encodeHello, encodeInput, encodePing, encodeUpgrade, encodeClanReq, encodeMarkReq, encodeRaidStart, encodeMarketReq, encodeWalletReq, encodeCraft, encodeConsume, encodeLoadGoods, encodeEscortReq, encodeFreightReq, encodeAuctionReq,
  decodeCasino, encodeCasinoReq,
  EventType, WhaleState,
  decodePortWelcome, decodePortSnapshot, decodePortPop, encodeWalkInput, encodePortReq, PortEventType, PortOp, AvatarFlag,
  PORT, CAPITAL, isCapitalOutpost,
  RoomKind, decodeHouseState, decodeHouseEditRes, encodeHouseReq, encodeHouseEdit,
  decodeChat, decodeRtc, decodeAnnounce, encodeChatReq, encodeRtcReq, type ChatMsg, type RtcMsg, type RtcReqMsg, type AnnounceMsg,
  decodeGachaState, encodeGachaReq, type GachaStateMsg,
  decodeExchangeState, type ExchangeStateMsg,
  type ClanInfoMsg, type ClanMarkerWire, type InputMsg, type LbEntry, type LbClanEntry, type LbRecruitEntry, type RaidResultMsg, type SelfState, type Snapshot, type SnapshotShip, type SnapshotBall,
  type SnapshotEvent, type WireBase, type WorldMsg, type MarketStateMsg, type GoodsEntry, type JournalEntry, type FreightWire, type AuctionWire, type WarEntryWire, type RevengeContractWire, type SeasonMsg, type CodexCatWire, type DawnMsg, type CalendarMsg, type ProfLeaderEntry,
  type PortAvatar, type PortSelf, type WalkInputMsg, type HouseStateMsg, type HouseEditMsg, type HouseEditResMsg,
} from '@piratas/protocol';
import { telemetryConnect, telemetryFunnel, telemetryMarkReconnect } from './telemetry.js';

export interface InterpShip {
  id: number; x: number; y: number; heading: number; speed: number; sail: number; hp: number; flags: number; cls: number;
}
export interface InterpBall { id: number; x: number; y: number; heading: number; elev: number; }

interface StoredSnap {
  t: number; // server sim time ms
  ships: Map<number, SnapshotShip>;
  balls: Map<number, SnapshotBall>;
}

type FxEvent = Extract<SnapshotEvent, { t: typeof EventType.HIT | typeof EventType.SINK | typeof EventType.FIRE | typeof EventType.RAM | typeof EventType.GRAPPLE_START | typeof EventType.GRAPPLE_END | typeof EventType.PLUNDER }>;

/** A stable, secret per-device identity persisted in localStorage. The server hashes it into the
 *  profileId so a player's base + economy is bound to THIS device, not to a guessable display name.
 *  Falls back to an empty string (ephemeral guest, persists nothing) when storage is blocked. */
function deviceToken(): string {
  const KEY = 'soh_device_token';
  try {
    let t = localStorage.getItem(KEY);
    if (!t || t.length < 8) {
      const b = new Uint8Array(16); crypto.getRandomValues(b);
      t = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
      localStorage.setItem(KEY, t);
    }
    return t;
  } catch { return ''; } // private mode / storage disabled → ephemeral guest
}

export class Net {
  ws: WebSocket | null = null;
  yourId = 0;
  worldSize = 8192; // fallback until S_WELCOME arrives (server is authoritative over the real size)
  world: WorldMsg | null = null;
  bases: WireBase[] = [];
  yourIslandId = -1;
  /** Bumped whenever the base set/state changes, so the renderer re-syncs. */
  basesVersion = 0;
  /** Own progression (from S_PROFILE). speedMul/turnMul drive local prediction. */
  loadout = { hullLvl: 1, speedLvl: 1, damageLvl: 1, cargoLvl: 1 };
  speedMul = 1;
  turnMul = 1;
  /** Sinks P3: resolved hold capacity from S_PROFILE (INCLUDING the cargo charm) — the prediction weight
   *  divisor, so a charmed hull predicts identically to the server. 0 = legacy/unknown → fall back to cargoOf(). */
  cargoCap = 0;
  /** economy-gaps: the per-player "auto-load the warehouse when berthed home" preference (S_PROFILE echo,
   *  default ON) — drives the Home Port toggle. Set optimistically by setCargoAutoLoad(). */
  cargoAutoLoad = true;
  /** Own ship class (ShipClassId) from S_PROFILE — drives local render scale + HUD. */
  shipClass = 1;
  /** Onda Avatares: own chosen cosmetic captain look (AVATAR_SKINS id, from S_PROFILE) — drives the local
   *  walker's skin + the Captain's Look picker's current selection. */
  mySkin = 0;
  /** Bitmask of unlocked ship classes (bit i = ShipClassId i owned) — drives the shipyard UI. */
  unlockedMask = 1;
  trophies = 0;
  /** Wave 3.3: mounted figurehead trophy id + bitmask of earned trophies (from S_PROFILE). */
  equippedTrophy = 0;
  trophyMask = 0;
  profileVersion = 0;
  clan: ClanInfoMsg | null = null;
  clanVersion = 0;
  /** Clan chart markers (v54) — the FULL current pin list for MY clan (server-synced; empty when clanless). */
  marks: ClanMarkerWire[] = [];
  marksVersion = 0;
  /** One-shot EN note from the last marker op (cap hit / permission refusal) — drained into a toast. */
  marksNote = '';
  leaderboard: LbEntry[] = [];
  leaderboardClans: LbClanEntry[] = [];
  /** Growth / Recruiters (v45): the Top Recruiters ladder (name + count) — rendered in the Captain's Book. */
  recruiters: LbRecruitEntry[] = [];
  leaderboardVersion = 0;
  /** Growth / Recruiters (v45): how many captains YOU recruited who reached the milestone (self-only, from S_PROFILE). */
  myRecruits = 0;
  /** Queued "your base is under live raid" alerts (x,y = base pos) — drained by the main loop into a toast + ping. */
  readonly raidWarnQueue: { x: number; y: number }[] = [];
  /** Queued clan map pings (x,y) — drained by the main loop into a fading minimap marker. */
  readonly clanPingQueue: { x: number; y: number }[] = [];
  /** The active Cursed Treasure (map-wide convergence event), or null — drives the mesh + minimap marker. */
  cursedChest: { x: number; y: number } | null = null;
  /** Queued cursed spawn/claim events — drained by the main loop into toast + ping (spawn) / FX (claim). */
  readonly cursedSpawnQueue: { x: number; y: number }[] = [];
  readonly cursedClaimQueue: { x: number; y: number }[] = [];
  /** Region control: regionId → controlling clan tag. */
  readonly territory = new Map<number, string>();
  territoryVersion = 0;
  /** Linked SOLANA wallet state (from S_WALLET): `address` is a base58 pubkey (case-sensitive — never fold
   *  it), and `nonce` is the one-shot challenge the ed25519 link signature covers. */
  wallet = { linked: false, address: '', nonce: '' };
  walletVersion = 0;
  /** Today's contracts (from S_CONTRACTS). */
  contracts: { t: number; goal: number; progress: number; reward: number; claimed: boolean }[] = [];
  contractsVersion = 0;
  premium: { enabled: boolean; boostRemainingS: number; insured: boolean; note: string } = { enabled: false, boostRemainingS: 0, insured: false, note: '' };
  premiumVersion = 0;
  // v57 (Solana): `mint` = the SPL mint of $SOR, `recipient` = the wallet that receives pack/license
  // payments (was `tokenAddress`/`routerAddress` — there is no router contract anymore). `vault` is the
  // SEPARATE custodial wallet a captain FUNDS to get a Royal Bank balance; '' while the exchange is disarmed.
  // `dexUrl` is the pump.fun coin page ("View on pump.fun").
  sor: { gateOn: boolean; licensed: boolean; claimsOn: boolean; eligible: number; pendingSorX100: number; licensePriceSor: number; mint: string; recipient: string; rpcUrl: string; dexUrl: string; note: string; vault: string } | null = null;
  sorVersion = 0;
  wind = { dir: 0, strength: 1 };
  stormIntensity = 0; // 0 calm … 1 full storm (server-authoritative, from the snapshot header)
  self: SelfState | null = null;
  ackSeq = 0;
  selfAuth: SnapshotShip | null = null;
  rttMs = 0;
  connected = false;
  /** Incremented on every ingested snapshot — lets the loop reconcile once per snapshot. */
  snapSeq = 0;
  readonly names = new Map<number, string>();
  readonly tags = new Map<number, string>();   // clan tag per ship (from EV_SPAWN), '' = no clan
  /** Per-ship class (ShipClassId), cached from SPAWN events. */
  readonly shipClasses = new Map<number, number>();
  /** Per-ship equipped cosmetics (hull/sail/flag ids) + figurehead trophy, cached from SPAWN events. */
  readonly cosmetics = new Map<number, { hull: number; sail: number; flag: number; trophy: number }>();
  /** Onda Frota (v47): per-ship name-badge bits (NAME_BADGE: bit0 DEV tag, bit1 gold name), cached from SPAWN
   *  events — drives the golden/DEV nametag OTHER captains see at sea. */
  readonly badges = new Map<number, number>();
  /** Own marketplace state (from S_MARKET); marketVersion bumps so the UI re-renders on change. */
  market: MarketStateMsg | null = null;
  marketVersion = 0;
  /** P2P ⓓ↔$SOR EXCHANGE view (from S_EXCHANGE — "The Royal Bank" chapter). `open:false` = disarmed/closed;
   *  the chapter stays HIDDEN from the Book until a reply lands with `open:true`. `exchangeAt` is the perf.now()
   *  the last state landed, so the UI can tick the RELATIVE hold/expiry seconds DOWN locally between fetches. */
  exchange: ExchangeStateMsg | null = null;
  exchangeVersion = 0;
  exchangeAt = 0;
  /** Gacha "A Draga" state (from S_GACHA): the pity meter, shards, owned amulets + the last roll result.
   *  gachaVersion bumps so the shop UI re-renders; onGacha fires so main.ts can launch the ritual. */
  gacha: GachaStateMsg | null = null;
  gachaVersion = 0;
  onGacha: ((m: GachaStateMsg) => void) | null = null;
  /** ---- Wave 2b: the ship HOLD + base MATERIAL inventory (from S_GOODS, owner-only on change). */
  goods: { hold: GoodsEntry[]; base: GoodsEntry[] } = { hold: [], base: [] };
  goodsVersion = 0;
  /** Fishing feedback (owner-only events), drained by the main loop into FX/audio/toasts. */
  readonly fishBiteQueue: number[] = [];
  readonly fishCatchQueue: { tier: number; item: number }[] = [];
  /** Active dynamic (player-sunk) wrecks, id → position — drives the divable-wreck meshes (2b.3). */
  readonly dynamicWrecks = new Map<number, { x: number; y: number }>();
  dynamicWrecksVersion = 0;
  /** Profession XP by track (from S_PROFESSIONS); level/perk derived client-side. */
  professions: Record<string, number> = {};
  professionsVersion = 0;
  /** Wave 3.1: drifting Messages in a Bottle, id → position — drives the bottle meshes. */
  readonly bottles = new Map<number, { x: number; y: number }>();
  bottlesVersion = 0;
  /** Wave 3.1: fished-up bottle reveals (owner-only), drained by the loop into a parchment note toast. */
  readonly bottleMsgQueue: { kind: number; text: string }[] = [];
  /** Wave 3.2: the Sea Journal feed (from S_JOURNAL). */
  journal: JournalEntry[] = [];
  journalVersion = 0;
  /** Wave 3.11: the P2P freight board (from S_FREIGHT) + a note to toast. */
  freight: FreightWire[] = [];
  freightNote = '';
  freightVersion = 0;
  /** Wave 3.12: the Capital auction book (from S_AUCTION) + a note. */
  auctions: AuctionWire[] = [];
  auctionNote = '';
  auctionVersion = 0;
  /** Wave 7: the last casino game view (from S_CASINO), keyed by station; bumps casinoVersion on change. */
  casino: { stationId: number; game: number; ok: boolean; view: Record<string, unknown> } | null = null;
  casinoVersion = 0;
  /** Wave 7: a rolling "Live Wins" feed of big casino payouts across the plaza (drained into toasts). */
  readonly casinoFeed: { name: string; game: string; profit: number; at: number }[] = [];
  /** Wave 7: this session's provably-fair client seed (mixed into every casino roll's HMAC). */
  readonly casinoClientSeed = (() => { const b = new Uint8Array(8); crypto.getRandomValues(b); return Array.from(b, (x) => x.toString(16).padStart(2, '0')).join(''); })();
  /** Wave 3.5: the White Whale marker (null = not hunting). state: WhaleState; hp 0..1 of its health. */
  whale: { x: number; y: number; heading: number; hp: number; surfaced: boolean } | null = null;
  /** Wave 3.5: whale announces (spawn/dead/gone) drained by the loop into a toast + SFX + ping. */
  readonly whaleQueue: { state: number }[] = [];
  /** Wave 3.6: WANTED alerts (a captain crossed the bounty threshold), drained into a red banner + SFX. */
  readonly bountyQueue: { id: number; bounty: number }[] = [];
  /** Wave 3.8: the Eye of the Storm calm zone (null = none). */
  eye: { x: number; y: number } | null = null;
  /** Wave 3.9: an active treasure-map dig site (client-derived; null = none). */
  digX: { x: number; y: number } | null = null;
  /** Wave 3.9: treasure-dug payout toasts (owner-only). `gold` = what actually fit in the hold; `capped` =
   *  the hoard overflowed a full cargo so some was lost (Onda Carona: warn instead of silently capping). */
  readonly digQueue: { gold: number; capped: boolean }[] = [];
  /** Onda Carona: island chests currently hoisted-and-gone, by chestId. Cleared on each S_WORLD (which ships
   *  every chest as present), then fed by CHEST_TAKEN/RESPAWN events + the pending CHEST_TAKEN a late joiner
   *  receives. `chestStateVersion` bumps on every change so the renderer re-applies visibility only on change. */
  readonly takenChests = new Set<number>();
  chestStateVersion = 0;
  /** Wave 5.1: the War Journal (raids suffered) + active revenge contracts + a one-shot note to toast. */
  warJournal: WarEntryWire[] = [];
  revengeContracts: RevengeContractWire[] = [];
  warJournalNote = '';
  warJournalVersion = 0;
  /** Wave 5.1: revenge-target markers (owner-only) — drained into a minimap ping + a hunt toast. */
  readonly revengeMarkQueue: { x: number; y: number }[] = [];
  /** Wave 5.2: the Season Pass state (from S_SEASON). */
  season: SeasonMsg | null = null;
  seasonVersion = 0;
  seasonNote = '';
  /** Wave 5.3: the Captain's Codex discovery state (from S_CODEX). */
  codex: CodexCatWire[] = [];
  codexVersion = 0;
  codexNote = '';
  /** Wave 5.4a: the Dawn Report, shown once on login (null until/after consumed). */
  dawn: DawnMsg | null = null;
  /** Wave 5.4d: today's weekly-calendar world event (from S_CALENDAR). */
  calendar: CalendarMsg | null = null;
  calendarVersion = 0;
  /** Wave 5.4e: this week's per-profession ladders (PROFESSION_KEYS order, from S_PROFLEADER). */
  profLeaders: ProfLeaderEntry[][] = [];
  profLeadersVersion = 0;
  /** Wave 5.4c: kill-streak announces (map-wide) — drained into a banner + SFX. */
  readonly killStreakQueue: { id: number; count: number }[] = [];

  // ---- Wave 6: the walkable port ----
  /** True while ashore in a port instance (drives the whole client into "port mode"). */
  portMode = false;
  /** Which outpost's port we're in (0 = at sea) — the market venue + the exit ring. */
  portOutpost = 0;
  /** Wire quantization span of the current walking room (96 m capital / 64 m village+house) — must mirror
   *  the server's `layout.size` exactly (Onda Parede-invisível-4; see packPortPos). */
  portPlazaSize: number = PORT.SIZE;
  /** Our avatar id inside the port instance. */
  yourAvatarId = 0;
  /** Bumped on every S_PORTWELCOME / return-to-sea, so the loop can (re)build the port scene. */
  portModeVersion = 0;
  /** Bumped on each ingested port snapshot — lets the loop reconcile once per snapshot. */
  portSnapSeq = 0;
  portAckSeq = 0;
  /** Authoritative self avatar body (drives local prediction reconcile). */
  portSelf: PortSelf | null = null;
  /** "N captains in port" per outpost (index = outpostId − 1), from S_PORTPOP. */
  portPop: number[] = [];
  /** Avatar identity/cosmetic caches (ride EV spawn, like ships). */
  readonly avatarNames = new Map<number, string>();
  readonly avatarCls = new Map<number, number>();
  /** Onda Avatares: per-walker chosen skin (AVATAR_SKINS id), cached from the SPAWN event like avatarCls. A
   *  live re-skin re-emits SPAWN, so this map just overwrites — the port/house scene rebuilds that avatar. */
  readonly avatarSkins = new Map<number, number>();
  /** Onda Frota (v47): per-walker name-badge bits (DEV tag + gold name), cached from the SPAWN event — drives
   *  the golden/DEV nametag in port + house scenes. */
  readonly avatarBadges = new Map<number, number>();
  /** Emote plays (avatarId → emote id), drained by the port scene into a one-shot clip. */
  readonly portEmoteQueue: { id: number; emote: number }[] = [];
  /** v43 Synced Sit: sit/stand events (avatarId + on + seat transform), drained by the port/house scene so a
   *  remote captain shows seated on the exact chair/bench. Held state — the scene keeps the pose until a stand. */
  readonly portSitQueue: { id: number; on: number; x: number; y: number; heading: number; y0: number }[] = [];
  private portSnaps: { t: number; avatars: Map<number, PortAvatar> }[] = [];

  // ---- Wave 8: the captain's house (a walkable interior instance; a "port" with a different scene) ----
  /** Which room kind the current port instance is (RoomKind.PORT=0 / HOUSE=1) — the loop branches on this. */
  roomKind: number = RoomKind.PORT;
  /** The current house interior state (from S_HOUSE_STATE) — null at sea / in a normal port. */
  house: HouseStateMsg | null = null;
  /** Bumped on each S_HOUSE_STATE so the scene/HUD re-sync + prediction re-seeds solid furniture. */
  houseVersion = 0;
  /** S_HOUSE_EDIT results (ok ack / reject reason) — drained by the loop into toasts (decorate + ENTER rejects). */
  readonly houseEditQueue: HouseEditResMsg[] = [];

  private snaps: StoredSnap[] = [];
  private serverOffset = 0; // performance.now() + serverOffset ~= server sim ms
  private offsetInit = false;
  private fxQueue: FxEvent[] = [];
  private raidResults: RaidResultMsg[] = [];

  onReady: (() => void) | null = null;
  onReject: ((reason: string) => void) | null = null;
  onClose: (() => void) | null = null;
  /** Onda Social (v40): a routed chat line arrived (subscribed by the chat panel). */
  onChat: ((m: ChatMsg) => void) | null = null;
  /** Onda Social (v40): a voice-signalling packet arrived (subscribed by the voice engine). */
  onRtc: ((m: RtcMsg) => void) | null = null;
  /** ⚓ Captain's Orders (v46): an admin broadcast BANNER arrived (subscribed by main.ts → hud.announceBanner). */
  onAnnounce: ((m: AnnounceMsg) => void) | null = null;
  /** Auto-reconnect UI hooks — a live link dropped mid-game and we're retrying (attempt N) / we're back. */
  onReconnecting: ((attempt: number) => void) | null = null;
  onReconnected: (() => void) | null = null;
  onReconnectFailed: (() => void) | null = null;
  /** Initial-handshake UI hooks (before the FIRST welcome) — the "entering the sea" progress screen + its failure
   *  paths. `onConnectProgress('connecting')` fires while the socket is opening; `('entering')` once it's open and
   *  we're awaiting the welcome + world sync. These give the boot→spawn path a live, diegetic status instead of a
   *  mute "connecting…". */
  onConnectProgress: ((stage: 'connecting' | 'entering') => void) | null = null;
  /** A handshake attempt failed (timeout / early drop) and we're backing off for another try (attempt N of max). */
  onConnectRetry: ((attempt: number, max: number) => void) | null = null;
  /** Every initial attempt was spent — the sea is unreachable. The menu's SET SAIL stays live as the manual retry. */
  onConnectFailed: (() => void) | null = null;
  /** The server rejected us for a protocol/version mismatch — a stale cached client vs a bumped wire. The app shows
   *  "new version" and hard-reloads (nginx serves index.html no-cache, so the reload pulls the fresh bundle). */
  onVersionReject: (() => void) | null = null;

  /** WebSocket constructor, injectable so the handshake state machine is unit-testable with a mock socket +
   *  fake timers. Defaults to the browser global (evaluated lazily, so importing net.ts under node never throws). */
  wsFactory: (url: string) => WebSocket = (url) => new WebSocket(url);

  private url = '';
  private connName = '';
  private intentional = false; // set on a deliberate disconnect() so we DON'T auto-reconnect
  private everWelcomed = false; // only auto-reconnect AFTER a successful handshake (menu retries the first connect)
  /** Has this connection ever been welcomed? Read by the app to choose WHERE a terminal message can be seen: the
   *  menu's `#status` line is removed from the DOM once the sea starts, so an in-game refusal needs the HUD banner. */
  get welcomed(): boolean { return this.everWelcomed; }
  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private static readonly MAX_RECONNECT = 15; // ~100s of backoff before we give up and ask for a reload
  // ---- initial-handshake robustness (before the first welcome) ----
  private connectAttempt = 0;   // how many fresh handshakes have failed this connect() (drives the backoff + the give-up)
  private connectGaveUp = false; // latched once we surface onConnectFailed — keeps the give-up idempotent (no double-fire)
  private gotReject = false;    // the server sent an explicit S_REJECT — its UI owns the retry, so don't auto-hammer it
  private handshakeTimer: ReturnType<typeof setTimeout> | null = null; // open/welcome watchdog (a dead "connecting…" screen killer)
  private handshakePhase: 'idle' | 'opening' | 'awaiting_welcome' = 'idle';
  private static readonly MAX_CONNECT_ATTEMPTS = 4; // 1 initial + 3 retries before we surface "couldn't reach the sea"
  private static readonly OPEN_TIMEOUT_MS = 8000;    // socket must OPEN within this (bad 4G / dropped SYN)
  private static readonly WELCOME_TIMEOUT_MS = 8000;  // …then the welcome must arrive within this (stalled join)
  private static readonly CONNECT_BACKOFF = [800, 1600, 3200, 5000]; // exponential-ish, capped

  connect(url: string, name: string): void {
    this.url = url; this.connName = name;
    this.intentional = false; this.everWelcomed = false; this.reconnectAttempt = 0;
    this.connectAttempt = 0; this.connectGaveUp = false; this.gotReject = false;
    if (this.reconnectTimer) { clearTimeout(this.reconnectTimer); this.reconnectTimer = null; }
    telemetryConnect('start'); // funnel: SET SAIL tapped — the denominator for "did the handshake complete?"
    this.openSocket();
  }

  /** The captain's server-authoritative display name (from their own EV_SPAWN), falling back to the name they
   *  set sail with. Used by the "sail with me" invite link so a friend's `?crew=` matches what others see. */
  myDisplayName(): string { return this.names.get(this.yourId) || this.connName || 'Captain'; }

  /** This device's stable secret identity (the same token sent in C_HELLO). The SOR Store HTTP endpoint
   *  hashes it into the profileId to bind an order to this captain. Same-origin only. */
  getDeviceToken(): string { return deviceToken(); }

  /** (Re)open the socket and re-handshake with the SAME device token — the server restores the
   *  same profile/base on rejoin, so a dropped link resumes the exact identity, not a new account. */
  private openSocket(): void {
    let ws: WebSocket;
    try { ws = this.wsFactory(this.url); }
    catch { this.handleClose(); return; } // malformed URL / blocked constructor → treat as a failed attempt (retry)
    ws.binaryType = 'arraybuffer';
    this.ws = ws;
    if (!this.everWelcomed) this.onConnectProgress?.('connecting'); // "catching the wind…" — the socket is opening
    this.armHandshakeTimeout('opening');
    ws.onopen = () => {
      if (this.ws !== ws) return; // a superseded socket (a retry already replaced us)
      this.armHandshakeTimeout('awaiting_welcome'); // re-arm: now the WELCOME must arrive
      if (!this.everWelcomed) { this.onConnectProgress?.('entering'); telemetryFunnel('socket_open'); } // "entering the sea…" — hello sent, awaiting the world
      ws.send(encodeHello({ name: this.connName, token: deviceToken() }));
    };
    ws.onmessage = (ev) => this.onMessage(new Uint8Array(ev.data as ArrayBuffer));
    ws.onclose = () => { if (this.ws !== ws) return; this.connected = false; this.handleClose(); };
    ws.onerror = () => { /* surfaced via onclose */ };
  }

  /** Arm the handshake watchdog for the given phase. On expiry we close the socket (which drives the normal
   *  onclose → retry path) and beacon which phase stalled, so a bad network can never leave the captain staring at
   *  a mute "connecting…" forever. Cleared the instant the first welcome lands. */
  private armHandshakeTimeout(phase: 'opening' | 'awaiting_welcome'): void {
    this.clearHandshakeTimer();
    this.handshakePhase = phase;
    const ms = phase === 'opening' ? Net.OPEN_TIMEOUT_MS : Net.WELCOME_TIMEOUT_MS;
    this.handshakeTimer = setTimeout(() => {
      this.handshakeTimer = null;
      telemetryConnect(phase === 'opening' ? 'timeout' : 'welcome_timeout');
      try { this.ws?.close(); } catch { /* already gone */ }
      // Some environments never fire onclose for a CONNECTING socket we abort — drive the retry ourselves too.
      // handleClose is idempotent-safe here: the timer is null and everWelcomed is false, so it just schedules once.
      this.handleClose();
    }, ms);
  }

  private clearHandshakeTimer(): void {
    if (this.handshakeTimer) { clearTimeout(this.handshakeTimer); this.handshakeTimer = null; }
    this.handshakePhase = 'idle';
  }

  private handleClose(): void {
    if (this.intentional) { this.onClose?.(); return; } // deliberate quit
    // An explicit S_REJECT is TERMINAL — welcomed or not. Reconnecting into a refusal can only flap, and for the
    // duplicate-session refusal it was a two-tab WAR: the bumped tab is welcomed BY DEFINITION, so it fell through
    // to the reconnect branch below, came back 500 ms later, and bumped the tab that had just displaced it — which
    // then bumped back. Both tabs unplayable at ~1–2 Hz, with a full server-side teardown+persist (saveProfile /
    // saveBase / casino cash-out / pendingHold mirror) on every lap, and `MAX_RECONNECT` never capping it because
    // `reconnectAttempt` resets on every S_WELCOME. The same latch also makes an admin kick actually stick.
    if (this.gotReject) { this.clearHandshakeTimer(); this.onClose?.(); return; }
    if (!this.everWelcomed) {
      // The FIRST handshake never completed. Don't leave a dead screen: auto-retry with backoff (unless the server
      // gave an explicit reject, whose own UI owns the retry). onClose still fires so the app can freeze input.
      this.clearHandshakeTimer();
      this.onClose?.();
      if (!this.gotReject) this.scheduleConnectRetry();
      return;
    }
    this.onClose?.();       // a live, welcomed session dropped — the in-game reconnect flow takes over
    this.scheduleReconnect();
  }

  /** Initial-connect retry: a fresh handshake failed before the first welcome. Back off and try again, up to
   *  MAX_CONNECT_ATTEMPTS, then surface a clear terminal failure (the menu's SET SAIL is the manual retry). */
  private scheduleConnectRetry(): void {
    if (this.reconnectTimer || this.connectGaveUp) return; // one attempt already pending, or we've already given up
    this.connectAttempt++;
    if (this.connectAttempt >= Net.MAX_CONNECT_ATTEMPTS) { this.connectGaveUp = true; this.onConnectFailed?.(); return; }
    telemetryConnect('retry');
    const delay = Net.CONNECT_BACKOFF[Math.min(this.connectAttempt - 1, Net.CONNECT_BACKOFF.length - 1)]!;
    this.onConnectRetry?.(this.connectAttempt, Net.MAX_CONNECT_ATTEMPTS);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.resetSession();
      this.openSocket();
    }, delay);
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) return; // one attempt already pending
    telemetryMarkReconnect(); // a live, welcomed session dropped → tag its session_end 'reconnect' (blip, not a fresh open)
    if (this.reconnectAttempt >= Net.MAX_RECONNECT) { this.onReconnectFailed?.(); return; }
    const DELAYS = [500, 1000, 2000, 4000, 8000];
    const delay = DELAYS[Math.min(this.reconnectAttempt, DELAYS.length - 1)]!;
    this.reconnectAttempt++;
    this.onReconnecting?.(this.reconnectAttempt);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.resetSession();
      this.openSocket();
    }, delay);
  }

  /** Wipe transient per-connection state before a reconnect so the fresh session interpolates
   *  cleanly — stale snapshots carry the old server-time offset + old ship ids and would corrupt it. */
  private resetSession(): void {
    this.snaps = [];
    this.offsetInit = false;
    this.serverOffset = 0;
    this.fxQueue = [];
    this.raidResults = [];
    this.selfAuth = null;
    this.self = null;
    this.names.clear();
    this.tags.clear();
    this.shipClasses.clear();
    this.cosmetics.clear();
    this.badges.clear();
    // Ghost-entity cleanup (mobile reconnect fix): these EVENT-SOURCED world entities are re-sent by the rejoin
    // spawn burst for whatever is still ACTIVE — but anything that despawned during the drop never gets a despawn,
    // so without wiping here the renderer keeps a phantom wreck/bottle/whale/eye/cursed-chest. Bump the version
    // counters the loop watches so it re-syncs the (now empty) sets on the next frame; the rejoin burst repopulates.
    this.dynamicWrecks.clear(); this.dynamicWrecksVersion++;
    this.bottles.clear(); this.bottlesVersion++;
    this.takenChests.clear(); this.chestStateVersion++; // (S_WORLD also resets this; belt-and-suspenders during the gap)
    this.whale = null;
    this.eye = null;
    this.cursedChest = null;
    this.digX = null;
    // yourId / connected / world / bases are refreshed by the next S_WELCOME + S_WORLD + S_BASES
  }

  /** Deliberate disconnect (leaving the game) — suppresses auto-reconnect. */
  disconnect(): void {
    this.intentional = true;
    this.clearHandshakeTimer();
    if (this.reconnectTimer) { clearTimeout(this.reconnectTimer); this.reconnectTimer = null; }
    this.ws?.close();
  }

  private onMessage(data: Uint8Array): void {
    const type = data[0];
    const r = new BinaryReader(data, 1);
    switch (type) {
      case MsgType.S_WELCOME: {
        const w = decodeWelcome(r);
        this.clearHandshakeTimer(); // the handshake landed — disarm the watchdog
        const firstWelcome = !this.everWelcomed;
        this.yourId = w.yourId;
        this.worldSize = w.worldSize;
        this.connected = true;
        if (this.portMode) { // wave 6: sailed back out of a port instance → fresh sea baseline
          this.portMode = false; this.portModeVersion++;
          this.self = null; this.selfAuth = null; this.snaps = []; this.offsetInit = false;
          this.roomKind = RoomKind.PORT; this.house = null; this.houseVersion++; // wave 8: clear house state on return-to-sea
        }
        const wasReconnecting = this.everWelcomed && this.reconnectAttempt > 0;
        this.everWelcomed = true; this.reconnectAttempt = 0; this.connectAttempt = 0;
        if (firstWelcome) { telemetryConnect('ok'); telemetryFunnel('welcome'); } // funnel: the handshake completed — connection vs download/abandon split
        if (wasReconnecting) this.onReconnected?.(); // resumed a dropped session
        else this.onReady?.(); // first handshake — start the game; world layout streams in next
        break;
      }
      case MsgType.S_WORLD: {
        this.world = decodeWorld(r);
        telemetryFunnel('world'); // funnel: the static sea layout arrived (once-guarded; harmless on reconnect resync)
        // Onda Carona: S_WORLD ships every chest as present; the taken ones arrive right after as pending
        // CHEST_TAKEN events. Reset so a (re)join rebuilds chest state from the authoritative stream.
        this.takenChests.clear(); this.chestStateVersion++;
        break;
      }
      case MsgType.S_BASES: {
        const m = decodeBases(r);
        this.yourIslandId = m.yourIslandId;
        this.bases = m.bases;
        this.basesVersion++;
        break;
      }
      case MsgType.S_PROFILE: {
        const m = decodeProfile(r);
        this.loadout = { hullLvl: m.hullLvl, speedLvl: m.speedLvl, damageLvl: m.damageLvl, cargoLvl: m.cargoLvl };
        this.speedMul = m.speedMul; this.turnMul = m.turnMul; this.trophies = m.trophies;
        this.shipClass = m.shipClass; this.unlockedMask = m.unlockedMask;
        this.equippedTrophy = m.equippedTrophy; this.trophyMask = m.trophyMask;
        this.mySkin = m.avatarSkin;
        this.myRecruits = m.recruits;
        this.cargoCap = m.cargoCap ?? 0; // Sinks P3: resolved hold cap (incl. cargo charm) for weight prediction
        this.cargoAutoLoad = m.cargoAutoLoad !== false; // economy-gaps: the Home Port auto-load toggle state
        this.profileVersion++;
        break;
      }
      case MsgType.S_CLAN: {
        this.clan = decodeClan(r);
        this.clanVersion++;
        break;
      }
      case MsgType.S_MARKS: { // clan chart markers (v54): always the FULL list for MY clan (empty = clanless)
        const m = decodeMarks(r);
        this.marks = m.markers;
        if (m.note) this.marksNote = m.note; // one-shot toast, drained by the main loop
        this.marksVersion++;
        break;
      }
      case MsgType.S_LEADERBOARD: {
        { const lb = decodeLeaderboard(r); this.leaderboard = lb.entries; this.leaderboardClans = lb.clans; this.recruiters = lb.recruiters ?? []; }
        this.leaderboardVersion++;
        break;
      }
      case MsgType.S_TERRITORY: {
        this.territory.clear();
        for (const rg of decodeTerritory(r).regions) this.territory.set(rg.regionId, rg.tag);
        this.territoryVersion++;
        break;
      }
      case MsgType.S_WALLET: {
        this.wallet = decodeWallet(r);
        this.walletVersion++;
        break;
      }
      case MsgType.S_SOR: {
        this.sor = decodeSor(r);
        this.sorVersion++;
        break;
      }
      case MsgType.S_PREMIUM: {
        this.premium = decodePremium(r);
        this.premiumVersion++;
        break;
      }
      case MsgType.S_CONTRACTS: {
        this.contracts = decodeContracts(r).list;
        this.contractsVersion++;
        break;
      }
      case MsgType.S_RAID_RESULT: {
        this.raidResults.push(decodeRaidResult(r));
        break;
      }
      case MsgType.S_MARKET: {
        this.market = decodeMarketState(r);
        this.marketVersion++;
        break;
      }
      case MsgType.S_EXCHANGE: {
        this.exchange = decodeExchangeState(r);
        this.exchangeVersion++;
        this.exchangeAt = (typeof performance !== 'undefined' ? performance.now() : Date.now());
        break;
      }
      case MsgType.S_GOODS: {
        this.goods = decodeGoods(r);
        this.goodsVersion++;
        break;
      }
      case MsgType.S_PROFESSIONS: {
        const tracks = decodeProfessions(r).tracks;
        const map: Record<string, number> = {};
        PROFESSION_KEYS.forEach((k, i) => { map[k] = tracks[i] ?? 0; });
        this.professions = map;
        this.professionsVersion++;
        break;
      }
      case MsgType.S_JOURNAL: {
        this.journal = decodeJournal(r).entries;
        this.journalVersion++;
        break;
      }
      case MsgType.S_WARJOURNAL: {
        const m = decodeWarJournal(r);
        this.warJournal = m.entries; this.revengeContracts = m.contracts;
        if (m.note) this.warJournalNote = m.note;
        this.warJournalVersion++;
        break;
      }
      case MsgType.S_SEASON: {
        const m = decodeSeason(r);
        this.season = m; if (m.note) this.seasonNote = m.note;
        this.seasonVersion++;
        break;
      }
      case MsgType.S_CODEX: {
        const m = decodeCodex(r);
        this.codex = m.cats; if (m.note) this.codexNote = m.note;
        this.codexVersion++;
        break;
      }
      case MsgType.S_DAWN: {
        this.dawn = decodeDawn(r);
        break;
      }
      case MsgType.S_CALENDAR: {
        this.calendar = decodeCalendar(r);
        this.calendarVersion++;
        break;
      }
      case MsgType.S_PROFLEADER: {
        this.profLeaders = decodeProfLeader(r).tracks;
        this.profLeadersVersion++;
        break;
      }
      case MsgType.S_PORTWELCOME: {
        const w = decodePortWelcome(r);
        this.portMode = true;
        this.roomKind = w.kind ?? RoomKind.PORT; // wave 8: HOUSE=1 drives the HouseScene instead of the port
        this.house = null; // fresh interior; S_HOUSE_STATE follows for a house
        this.portOutpost = w.outpostId;
        this.yourAvatarId = w.yourId;
        this.portSelf = null;
        this.portSnaps = [];
        // Onda Parede-invisível-4: derive THIS room's wire quantization span exactly like the server does
        // (capital plaza = 96 m, village/house = 64 m). The village-fixed span used to clamp every capital
        // transform at 64 m — the invisible wall bisecting the city. Zero wire-format change (still u16).
        this.portPlazaSize = (this.roomKind === RoomKind.HOUSE || !isCapitalOutpost(w.outpostId)) ? PORT.SIZE : CAPITAL.SIZE;
        this.offsetInit = false; // re-seed the clock offset from the port's sim time
        this.avatarNames.clear(); this.avatarCls.clear(); this.avatarSkins.clear(); this.avatarBadges.clear();
        this.portEmoteQueue.length = 0;
        this.portSitQueue.length = 0;
        this.portModeVersion++;
        // Onda FTUE: a brand-new captain's VERY FIRST handshake is a HOUSE welcome (they spawn inside their own
        // cabin), not the sea S_WELCOME. Bootstrap the game here too — otherwise onReady never fires, the render
        // loop never starts, and they see a black screen. The onReady guard makes the later sea S_WELCOME a no-op.
        if (!this.everWelcomed) { this.clearHandshakeTimer(); this.everWelcomed = true; this.connected = true; this.connectAttempt = 0; telemetryConnect('ok'); telemetryFunnel('welcome'); this.onReady?.(); }
        break;
      }
      case MsgType.S_HOUSE_STATE: {
        this.house = decodeHouseState(r);
        this.houseVersion++;
        break;
      }
      case MsgType.S_HOUSE_EDIT: {
        this.houseEditQueue.push(decodeHouseEditRes(r));
        break;
      }
      case MsgType.S_PORTSNAP: {
        this.ingestPortSnapshot(decodePortSnapshot(r, this.portPlazaSize));
        break;
      }
      case MsgType.S_PORTPOP: {
        this.portPop = decodePortPop(r).counts;
        break;
      }
      case MsgType.S_FREIGHT: {
        const m = decodeFreight(r);
        this.freight = m.contracts; this.freightNote = m.note; this.freightVersion++;
        break;
      }
      case MsgType.S_AUCTION: {
        const m = decodeAuction(r);
        this.auctions = m.lots; this.auctionNote = m.note; this.auctionVersion++;
        break;
      }
      case MsgType.S_CASINO: {
        const m = decodeCasino(r);
        let view: Record<string, unknown> = {};
        try { view = JSON.parse(m.payload) as Record<string, unknown>; } catch { view = {}; }
        if (view.kind === 'feed') {
          this.casinoFeed.push({ name: String(view.name ?? '?'), game: String(view.game ?? 'the casino'), profit: Number(view.profit ?? 0), at: performance.now() });
          if (this.casinoFeed.length > 8) this.casinoFeed.shift();
        } else {
          this.casino = { stationId: m.stationId, game: m.game, ok: m.ok, view };
          this.casinoVersion++;
        }
        break;
      }
      case MsgType.S_SNAPSHOT: {
        this.ingestSnapshot(decodeSnapshot(r));
        break;
      }
      case MsgType.S_PONG: {
        const p = decodePong(r);
        this.rttMs = performance.now() - p.clientTimeMs;
        break;
      }
      case MsgType.S_CHAT: {
        this.onChat?.(decodeChat(r));
        break;
      }
      case MsgType.S_RTC: {
        this.onRtc?.(decodeRtc(r));
        break;
      }
      case MsgType.S_ANNOUNCE: {
        this.onAnnounce?.(decodeAnnounce(r));
        break;
      }
      case MsgType.S_GACHA: {
        const g = decodeGachaState(r);
        this.gacha = g;
        this.gachaVersion++;
        this.onGacha?.(g);
        break;
      }
      case MsgType.S_REJECT: {
        const reason = decodeReject(r).reason;
        this.clearHandshakeTimer();
        this.gotReject = true; // explicit server refusal — its own UI owns the retry; don't auto-hammer the server
        if (this.reconnectTimer) { clearTimeout(this.reconnectTimer); this.reconnectTimer = null; }
        if (/protocol mismatch|version/i.test(reason)) {
          // A stale cached client vs a bumped wire (v44). index.html is served no-cache, so a hard reload pulls the
          // fresh hashed bundle — the app shows "new version" and reloads instead of dead-ending on a raw reject.
          telemetryConnect('version');
          this.intentional = true; // this socket is a dead end — suppress the reconnect flow when it closes
          this.onVersionReject?.();
        } else {
          telemetryConnect('reject'); // sea-full / banned / join-failed — the friendly reject toast handles it
          this.onReject?.(reason);
        }
        break;
      }
      default: break;
    }
  }

  private ingestSnapshot(snap: Snapshot): void {
    // smoothed clock offset (server sim ms vs local perf clock)
    const sample = snap.serverTimeMs - performance.now();
    if (!this.offsetInit) { this.serverOffset = sample; this.offsetInit = true; }
    else this.serverOffset += (sample - this.serverOffset) * 0.05;

    this.wind = { dir: snap.windDir, strength: snap.windStrength };
    this.stormIntensity = snap.stormIntensity;
    this.self = snap.self;
    this.ackSeq = snap.ackSeq;
    this.digX = snap.self.digX > 0 || snap.self.digY > 0 ? { x: snap.self.digX, y: snap.self.digY } : null; // wave 3.9 treasure-map X

    const ships = new Map<number, SnapshotShip>();
    for (const s of snap.ships) { ships.set(s.id, s); if (s.id === this.yourId) this.selfAuth = s; }
    const balls = new Map<number, SnapshotBall>();
    for (const b of snap.balls) balls.set(b.id, b);
    this.snaps.push({ t: snap.serverTimeMs, ships, balls });
    if (this.snaps.length > 30) this.snaps.shift();
    this.snapSeq += 1;

    for (const e of snap.events) this.handleEvent(e);
  }

  /** Best estimate of current server sim time (ms) on the local clock. */
  serverNowMs(): number {
    return this.offsetInit ? performance.now() + this.serverOffset : performance.now();
  }

  /* ------------------------------- wave 6: port ------------------------------- */

  private ingestPortSnapshot(snap: ReturnType<typeof decodePortSnapshot>): void {
    const sample = snap.serverTimeMs - performance.now();
    if (!this.offsetInit) { this.serverOffset = sample; this.offsetInit = true; }
    else this.serverOffset += (sample - this.serverOffset) * 0.05;
    this.portSelf = snap.self;
    this.portAckSeq = snap.ackSeq;
    const avatars = new Map<number, PortAvatar>();
    for (const a of snap.avatars) avatars.set(a.id, a);
    this.portSnaps.push({ t: snap.serverTimeMs, avatars });
    if (this.portSnaps.length > 30) this.portSnaps.shift();
    this.portSnapSeq++;
    for (const e of snap.events) {
      if (e.t === PortEventType.SPAWN) { this.avatarNames.set(e.id, e.name); this.avatarCls.set(e.id, e.cls); this.avatarSkins.set(e.id, e.skin); this.avatarBadges.set(e.id, e.badge ?? 0); }
      else if (e.t === PortEventType.DESPAWN) { this.avatarNames.delete(e.id); this.avatarCls.delete(e.id); this.avatarSkins.delete(e.id); this.avatarBadges.delete(e.id); }
      else if (e.t === PortEventType.EMOTE) this.portEmoteQueue.push({ id: e.id, emote: e.emote });
      else if (e.t === PortEventType.SIT) this.portSitQueue.push({ id: e.id, on: e.on, x: e.x, y: e.y, heading: e.heading, y0: e.y0 });
    }
  }

  /** Interpolated remote avatars (excludes the local predicted avatar), INTERP_DELAY_MS behind the clock. */
  getInterpAvatars(): { id: number; x: number; y: number; heading: number; moving: boolean; cls: number; skin: number; name: string; badge: number }[] {
    const rt = performance.now() + this.serverOffset - INTERP_DELAY_MS;
    const n = this.portSnaps.length;
    if (n === 0) return [];
    let a = this.portSnaps[0]!, b: { t: number; avatars: Map<number, PortAvatar> } | null = null, alpha = 0;
    if (n > 1) {
      let found = false;
      for (let i = n - 1; i > 0; i--) {
        const pa = this.portSnaps[i - 1]!, pb = this.portSnaps[i]!;
        if (pa.t <= rt && rt <= pb.t) { a = pa; b = pb; const span = pb.t - pa.t; alpha = span > 0 ? (rt - pa.t) / span : 0; found = true; break; }
      }
      if (!found) a = rt < this.portSnaps[0]!.t ? this.portSnaps[0]! : this.portSnaps[n - 1]!;
    }
    const out: { id: number; x: number; y: number; heading: number; moving: boolean; cls: number; skin: number; name: string; badge: number }[] = [];
    for (const [id, av] of a.avatars) {
      if (id === this.yourAvatarId) continue;
      const bv = b?.avatars.get(id);
      const cls = this.avatarCls.get(id) ?? 0, skin = this.avatarSkins.get(id) ?? 0, name = this.avatarNames.get(id) ?? '', badge = this.avatarBadges.get(id) ?? 0;
      if (bv && b) out.push({ id, x: lerp(av.x, bv.x, alpha), y: lerp(av.y, bv.y, alpha), heading: lerpAngle(av.heading, bv.heading, alpha), moving: (bv.flags & AvatarFlag.MOVING) !== 0, cls, skin, name, badge });
      else out.push({ id, x: av.x, y: av.y, heading: av.heading, moving: (av.flags & AvatarFlag.MOVING) !== 0, cls, skin, name, badge });
    }
    return out;
  }

  /** Send one on-foot input step (mirrors sendInput). */
  sendWalk(m: WalkInputMsg): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(encodeWalkInput(m));
  }
  /** Enter/exit/emote intent (PortOp). */
  sendPort(op: number, arg = 0): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(encodePortReq({ op, arg }));
  }
  /** Onda Avatares: set the cosmetic captain look (AVATAR_SKINS id). Optimistically updates the local `mySkin`
   *  so the Captain's Look picker + local walker respond instantly; the server echoes S_PROFILE + a live SPAWN.
   *  Works from any room (sea/port/house) — C_PORT is handled by all three. */
  sendSkin(id: number): void {
    this.mySkin = id;
    this.sendPort(PortOp.SETSKIN, id);
  }
  /** economy-gaps: flip the "auto-load cargo when berthed home" preference. Optimistic local update (the
   *  Home Port toggle re-renders instantly); the server persists it and echoes the truth on S_PROFILE. */
  setCargoAutoLoad(on: boolean): void {
    this.cargoAutoLoad = on;
    this.profileVersion++;
    this.sendPort(PortOp.SET_CARGO_AUTOLOAD, on ? 1 : 0);
  }
  /** Wave 8: house intent — HouseOp.ENTER (arg = target base islandId, 0 = own dock; sent at sea) /
   *  TOGGLE_OPEN (arg 0/1) / KICK (arg = avatar id). */
  sendHouse(op: number, arg = 0): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(encodeHouseReq({ op, arg }));
  }
  /** Wave 8: a decorate/shop edit inside your own house (PLACE/MOVE/REMOVE/BUY). Server re-validates. */
  sendHouseEdit(m: HouseEditMsg): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(encodeHouseEdit(m));
  }

  private handleEvent(e: SnapshotEvent): void {
    switch (e.t) {
      case EventType.SPAWN: this.names.set(e.id, e.name); this.tags.set(e.id, e.tag); this.shipClasses.set(e.id, e.cls); this.cosmetics.set(e.id, { hull: e.hull, sail: e.sail, flag: e.flag, trophy: e.trophy }); this.badges.set(e.id, e.badge ?? 0); break;
      case EventType.DESPAWN: this.names.delete(e.id); this.shipClasses.delete(e.id); this.cosmetics.delete(e.id); this.badges.delete(e.id); break;
      case EventType.HIT:
      case EventType.SINK:
      case EventType.FIRE:
      case EventType.RAM:
      case EventType.GRAPPLE_START:
      case EventType.GRAPPLE_END:
      case EventType.PLUNDER: this.fxQueue.push(e); break;
      case EventType.RAID_WARN: this.raidWarnQueue.push({ x: e.x, y: e.y }); break;
      case EventType.CLAN_PING: this.clanPingQueue.push({ x: e.x, y: e.y }); break;
      case EventType.CURSED_SPAWN: this.cursedChest = { x: e.x, y: e.y }; this.cursedSpawnQueue.push({ x: e.x, y: e.y }); break;
      case EventType.CURSED_CLAIM: this.cursedChest = null; this.cursedClaimQueue.push({ x: e.x, y: e.y }); break;
      case EventType.FISH_BITE: this.fishBiteQueue.push(1); break;
      case EventType.FISH_CATCH: this.fishCatchQueue.push({ tier: e.tier, item: e.item }); break;
      case EventType.WRECK_SPAWN: this.dynamicWrecks.set(e.id, { x: e.x, y: e.y }); this.dynamicWrecksVersion++; break;
      case EventType.WRECK_DESPAWN: this.dynamicWrecks.delete(e.id); this.dynamicWrecksVersion++; break;
      case EventType.BOTTLE_SPAWN: this.bottles.set(e.id, { x: e.x, y: e.y }); this.bottlesVersion++; break;
      case EventType.BOTTLE_DESPAWN: this.bottles.delete(e.id); this.bottlesVersion++; break;
      case EventType.BOTTLE_MSG: this.bottleMsgQueue.push({ kind: e.kind, text: e.text }); break;
      case EventType.WHALE:
        if (e.state === WhaleState.DEAD || e.state === WhaleState.GONE) { this.whale = null; this.whaleQueue.push({ state: e.state }); }
        else { this.whale = { x: e.x, y: e.y, heading: e.heading, hp: e.hp / 255, surfaced: e.state !== WhaleState.DIVE }; if (e.state === WhaleState.SPAWN) this.whaleQueue.push({ state: e.state }); }
        break;
      case EventType.BOUNTY: this.bountyQueue.push({ id: e.id, bounty: e.bounty }); break;
      case EventType.EYE: this.eye = e.active ? { x: e.x, y: e.y } : null; break;
      case EventType.DIG: this.digQueue.push({ gold: e.gold, capped: e.capped }); break;
      case EventType.REVENGE_MARK: this.revengeMarkQueue.push({ x: e.x, y: e.y }); break;
      case EventType.KILLSTREAK: this.killStreakQueue.push({ id: e.id, count: e.count }); break;
      case EventType.CHEST_TAKEN: this.takenChests.add(e.id); this.chestStateVersion++; break;
      case EventType.CHEST_RESPAWN: this.takenChests.delete(e.id); this.chestStateVersion++; break;
      default: break;
    }
  }

  /** Render-time in server sim ms, INTERP_DELAY_MS behind the latest server clock. */
  private renderTime(): number {
    return performance.now() + this.serverOffset - INTERP_DELAY_MS;
  }

  /** Interpolated remote ships (excludes local predicted ship). */
  getInterpShips(): InterpShip[] {
    const rt = this.renderTime();
    const { a, b, alpha } = this.bracket(rt);
    if (!a) return [];
    const out: InterpShip[] = [];
    for (const [id, sa] of a.ships) {
      if (id === this.yourId) continue;
      const cls = this.shipClasses.get(id) ?? 1;
      const sb = b?.ships.get(id);
      if (sb && b) {
        out.push({
          id,
          x: lerp(sa.x, sb.x, alpha),
          y: lerp(sa.y, sb.y, alpha),
          heading: lerpAngle(sa.heading, sb.heading, alpha),
          speed: lerp(sa.speed, sb.speed, alpha),
          sail: lerp(sa.sail, sb.sail, alpha),
          hp: sb.hp, flags: sb.flags, cls,
        });
      } else {
        out.push({ id, x: sa.x, y: sa.y, heading: sa.heading, speed: sa.speed, sail: sa.sail, hp: sa.hp, flags: sa.flags, cls });
      }
    }
    return out;
  }

  /** Interpolated cannonballs (all of them — they are fast and visual). */
  getInterpBalls(): InterpBall[] {
    const rt = this.renderTime();
    const { a, b, alpha } = this.bracket(rt);
    if (!a) return [];
    const out: InterpBall[] = [];
    for (const [id, ba] of a.balls) {
      const bb = b?.balls.get(id);
      if (bb) out.push({ id, x: lerp(ba.x, bb.x, alpha), y: lerp(ba.y, bb.y, alpha), heading: bb.heading, elev: ba.elev });
      else out.push({ id, x: ba.x, y: ba.y, heading: ba.heading, elev: ba.elev });
    }
    return out;
  }

  private bracket(t: number): { a: StoredSnap | null; b: StoredSnap | null; alpha: number } {
    const n = this.snaps.length;
    if (n === 0) return { a: null, b: null, alpha: 0 };
    if (n === 1) return { a: this.snaps[0]!, b: null, alpha: 0 };
    for (let i = n - 1; i > 0; i--) {
      const a = this.snaps[i - 1]!, b = this.snaps[i]!;
      if (a.t <= t && t <= b.t) {
        const span = b.t - a.t;
        return { a, b, alpha: span > 0 ? (t - a.t) / span : 0 };
      }
    }
    // t older than buffer -> oldest; t newer -> hold newest
    if (t < this.snaps[0]!.t) return { a: this.snaps[0]!, b: null, alpha: 0 };
    return { a: this.snaps[n - 1]!, b: null, alpha: 0 };
  }

  drainFx(): FxEvent[] { const q = this.fxQueue; this.fxQueue = []; return q; }
  nameOf(id: number): string { return this.names.get(id) ?? `#${id}`; }

  sendInput(m: InputMsg): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(encodeInput(m));
  }
  sendPing(): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(encodePing({ clientTimeMs: performance.now() }));
  }
  sendUpgrade(kind: number, target: number): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(encodeUpgrade({ kind, target }));
  }
  /** `signature` = the base58 Solana TRANSACTION signature of the payment (v57; was an EVM txHash). It is a
   *  HINT — the server re-reads the transaction and proves mint/recipient/amount/payer before granting. */
  sendSor(op: number, signature = '', amount = 0): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(encodeSorReq({ op, signature, amount }));
  }
  sendPremium(op: number, arg = 0): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(encodePremiumReq({ op, arg }));
  }
  sendWallet(op: number, address = '', signature = ''): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(encodeWalletReq({ op, address, signature }));
  }
  /** Wave 3.7: accept/abandon a merchant-convoy escort contract. */
  sendEscort(op: number): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(encodeEscortReq({ op }));
  }
  /** Wave 3.11: post/accept/cancel/refresh a P2P freight contract. */
  sendFreight(op: number, contractId = 0, materialId = 0, qty = 0, fee = 0, destOutpost = 0): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(encodeFreightReq({ op, contractId, materialId, qty, fee, destOutpost }));
  }
  /** Wave 3.12: list/bid/refresh a Capital auction lot. */
  sendAuction(op: number, auctionId = 0, materialId = 0, qty = 0, amount = 0): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(encodeAuctionReq({ op, auctionId, materialId, qty, amount }));
  }

  sendClan(op: number, name = '', tag = '', targetId = '', amount = 0, x = 0, y = 0): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(encodeClanReq({ op, name, tag, targetId, amount, x, y }));
  }
  /** Clan chart markers (v54): PLACE a pin at (x,y) with a glyph + optional label, or REMOVE by marker id.
   *  The server re-validates membership/cap/roles on every op — this is intent only. */
  sendMark(op: number, id = 0, icon = 0, x = 0, y = 0, label = ''): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(encodeMarkReq({ op, id, icon, x, y, label }));
  }
  /** Request an async (offline) raid — the server matchmakes a target and replies with S_RAID_RESULT. */
  sendRaid(): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(encodeRaidStart());
  }
  /** Wave 5.1: take revenge on a War Journal aggressor (by their profile id). */
  sendRevenge(targetId: string): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(encodeRevengeReq({ op: RevengeOp.START, targetId }));
  }
  /** Wave 5.2: claim a season reward / buy the premium track / refresh. */
  sendSeason(op: number, level = 0, track = 0): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(encodeSeasonReq({ op, level, track }));
  }
  /** Wave 5.3: claim a completed Codex set / refresh the book. */
  sendCodex(op: number, category = 0): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(encodeCodexReq({ op, category }));
  }
  sendMarket(op: number, a = 0, b = 0, c = 0): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(encodeMarketReq({ op, a, b, c }));
  }
  /** A Draga (v50): request state / pull / free-pull / shard-buy / equip-amulet. Server decides everything. */
  sendGacha(op: number, a = 0, b = 0): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(encodeGachaReq({ op, a, b }));
  }
  /** Wave 7: a casino intent (spin / deal / hit-stand / sit-fold-call-raise). The server resolves it. */
  sendCasino(op: number, stationId: number, bet = 0, arg = 0): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(encodeCasinoReq({ op, stationId, bet, arg, clientSeed: this.casinoClientSeed }));
  }
  /** Craft `count` of a recipe (CraftId) at the base (server validates docked + building). */
  sendCraft(recipe: number, count = 1): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(encodeCraft({ recipe, count }));
  }
  /** Activate a consumable buff (buffId = MaterialId). The item is spent from the base inventory. */
  sendConsume(buffId: number): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(encodeConsume({ buffId }));
  }
  /** farms-demand-first (v54): while berthed at home, load stockpile materials back INTO the hold — so they
   *  can be hauled to a public port craft station or the goods market. Server validates everything. */
  sendLoadGoods(materialId: number, qty: number): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(encodeLoadGoods({ materialId, qty }));
  }
  /** Onda Social (v40): send a composed chat line (channel + raw text). Server sanitises/routes. */
  sendChat(channel: number, text: string): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(encodeChatReq({ channel, text }));
  }
  /** Onda Social (v40): send a voice-signalling packet (HELLO/BYE/ARMADA/SIGNAL). */
  sendRtc(m: RtcReqMsg): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(encodeRtcReq(m));
  }

  /** Drain any raid outcomes received since the last frame (consumed by the HUD as toasts). */
  drainRaidResults(): RaidResultMsg[] {
    if (this.raidResults.length === 0) return [];
    const out = this.raidResults;
    this.raidResults = [];
    return out;
  }
}

function lerp(a: number, b: number, t: number): number { return a + (b - a) * t; }
function lerpAngle(a: number, b: number, t: number): number {
  let d = b - a;
  while (d > Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  return a + d * t;
}
